<template>
  <nav class="glass-nav" :style="{ marginLeft: shellOffset, transition: 'margin-left 0.25s ease' }">
    <button v-if="!isLoggedIn || isSidebarlessPage" type="button" class="nav-brand nav-brand-btn" @click="goHome"
      :aria-label="isLoggedIn ? 'BidSnap AI, go to Home' : 'BidSnap AI home page'">
      <span class="logo-glow" aria-hidden="true">
        <svg viewBox="0 0 64 64" width="32" height="32" fill="none">
          <defs>
            <linearGradient id="appnavg" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stop-color="#34f0c0" />
              <stop offset="1" stop-color="#0ea5e9" />
            </linearGradient>
          </defs>
          <!-- BidSnap mark: snap-frame corners capturing a checkmark -->
          <g stroke="url(#appnavg)" stroke-width="5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M23 15h-4a4 4 0 0 0-4 4v4" />
            <path d="M41 15h4a4 4 0 0 1 4 4v4" />
            <path d="M23 49h-4a4 4 0 0 1-4-4v-4" />
            <path d="M41 49h4a4 4 0 0 0 4-4v-4" />
          </g>
          <path d="M23 33.5l6.5 6.5L44 25" stroke="url(#appnavg)" stroke-width="7" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </span>
      <span class="brand-text-dark" aria-hidden="true">BidSnap<span class="brand-ai-gradient"> AI</span></span>
    </button>
    <button type="button" class="mobile-menu-btn" @click="toggleMobileMenu" aria-label="Menu"
      :aria-expanded="isMobileMenuOpen ? 'true' : 'false'" aria-controls="header-nav-links">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <line x1="3" y1="12" x2="21" y2="12"></line>
        <line x1="3" y1="6" x2="21" y2="6"></line>
        <line x1="3" y1="18" x2="21" y2="18"></line>
      </svg>
    </button>


    <div id="header-nav-links" class="nav-links" :class="{ 'mobile-open': isMobileMenuOpen }" style="margin-left: auto;">
      <!-- Signed in, the sidebar is the navigation. Signed out, one way back
           to the public site. -->
      <a v-if="!isLoggedIn" href="/" class="nav-item">Home</a>

      <div v-if="isLoggedIn" class="nav-item notification-wrapper" style="position: relative;"
        @keydown.esc="closeNotifications(true)">
        <button ref="notifTrigger" type="button" class="notification-trigger" @click="toggleNotification"
          :aria-label="`Notifications, ${unreadCount} unread`"
          :aria-expanded="showNotifications ? 'true' : 'false'" aria-controls="header-notification-panel">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
          <span v-if="unreadCount > 0" class="notification-badge" aria-hidden="true">{{ unreadCount }}</span>
        </button>

        <!-- Notification Dropdown -->
        <div v-if="showNotifications" id="header-notification-panel" class="notification-dropdown">
          <div class="notification-header">
            <span>Notifications</span>
            <button v-if="unreadCount > 0" type="button" class="mark-all-read" @click="markAllAsRead">Mark All Read</button>
          </div>
          <div class="notification-list">
            <div v-if="notifications.length === 0" class="no-notifications">
              No notifications.
            </div>
            <button v-for="note in notifications" :key="note.id" type="button"
                 :class="['notification-item-list', { unread: !note.is_read }]"
                 @click="handleNotificationClick(note)">
              <span class="note-dot" v-if="!note.is_read" aria-hidden="true"></span>
              <span class="note-body">
                <span class="note-msg">{{ note.message }}</span>
                <span class="note-time">{{ formatDate(note.created_date) }}<span v-if="!note.is_read" class="sr-only">, unread</span></span>
              </span>
            </button>
          </div>
          <button type="button" class="notification-footer" @click="goToNotificationCenter">
            View All Notifications
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </button>
        </div>
      </div>

      <!-- User Pill with Dropdown (logged in) / Login or Sign Up button (logged out) -->
      <div class="user-pill-container">
        <button v-if="isLoggedIn" type="button" class="user-pill" @click="goToAccount" title="Account">
          <span class="user-avatar" aria-hidden="true">{{ userInitials }}</span>
          <span>{{ userName || 'User' }}</span>
        </button>
        <!-- On the login page itself, offer Sign Up instead of Login -->
        <button v-else-if="$route.name === 'LoginPage'" type="button" class="header-login-btn" @click="goToSignUp">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <line x1="19" y1="8" x2="19" y2="14" />
            <line x1="22" y1="11" x2="16" y2="11" />
          </svg>
          Sign Up
        </button>
        <button v-else type="button" class="header-login-btn" @click="goToLogin">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
            <polyline points="10 17 15 12 10 7" />
            <line x1="15" y1="12" x2="3" y2="12" />
          </svg>
          Login
        </button>
      </div>
    </div>
  </nav>

  <!-- Plan-expiring-soon banner — dismissible per browser tab session -->
  <div v-if="isLoggedIn && showExpiryBanner" class="expiry-banner" role="status"
       :style="{ marginLeft: shellOffset, transition: 'margin-left 0.25s ease' }">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
    <span>Your plan expires in {{ daysUntilExpiry }} day{{ daysUntilExpiry === 1 ? '' : 's' }} — contact your administrator to renew.</span>
    <button type="button" class="expiry-banner-close" @click="dismissExpiryBanner" aria-label="Dismiss plan expiry notice">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
    </button>
  </div>

  <!-- Page-specific action bar — pages Teleport their title+buttons here -->
  <div id="page-action-bar" :style="{ marginLeft: shellOffset, transition: 'margin-left 0.25s ease' }"></div>

  <!-- Background-task completion toasts (slide in at top-right) -->
  <div class="toast-stack" aria-live="polite">
    <transition-group name="toast">
      <div v-for="toast in toasts" :key="toast.id"
           :class="['toast-card', toast.failed ? 'toast-failed' : 'toast-success']">
        <button type="button" class="toast-main" @click="onToastClick(toast)">
          <span class="toast-icon" aria-hidden="true">
            <svg v-if="!toast.failed" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6 9 17l-5-5"/></svg>
            <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          </span>
          <span class="toast-body">
            <span class="toast-title">{{ toast.title }}</span>
            <span class="toast-msg">{{ toast.message }}</span>
          </span>
        </button>
        <button type="button" class="toast-close" @click.stop="dismissToast(toast.id)" aria-label="Dismiss">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>
    </transition-group>
  </div>
</template>

<script>
import axios from 'axios';
import { isPlanExpiringSoon, getDaysUntilExpiry } from '../utils/auth';

export default {
  name: 'AppHeader',
  props: {
    userNameProp: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      isMobileMenuOpen: false,
      isLoggedIn: !!localStorage.getItem('user-info'),
      userName: 'User',
      showNotifications: false,
      notifications: [],
      unreadCount: 0,
      refreshInterval: null,
      // Toast popups for newly-arrived (background-task) notifications.
      toasts: [],
      // Highest notification id seen so far; new ids beyond this get a toast.
      // null until the first fetch so we don't toast historical notifications.
      lastSeenNotifId: null,
      // Plan-expiry warning banner — dismissible per session (sessionStorage),
      // reappears next time the tab is opened if the plan is still expiring soon.
      showExpiryBanner: false,
      daysUntilExpiry: null
    }
  },
  mounted() {
    this.fetchUserName();
    this.fetchNotifications();
    this.checkExpiryBanner();
    // Poll often so task-completion popups feel near real-time.
    this.refreshInterval = setInterval(this.fetchNotifications, 15 * 1000);
    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
    if (this.refreshInterval) clearInterval(this.refreshInterval);
  },
  watch: {
    // The header stays mounted across navigation (it's no longer hidden on
    // the login page), so refresh the auth state on every route change —
    // otherwise it would keep showing Login/Sign Up after a successful login.
    '$route'() {
      this.isLoggedIn = !!localStorage.getItem('user-info');
      if (this.isLoggedIn) {
        this.fetchUserName();
        this.checkExpiryBanner();
      }
    }
  },
  methods: {
    checkExpiryBanner() {
      // Dismissal is per browser-tab session — the banner comes back next visit
      // as long as the plan is still within the warning window (backend-driven,
      // see plan_service.EXPIRY_WARNING_DAYS).
      if (isPlanExpiringSoon() && !sessionStorage.getItem('expiry_banner_dismissed')) {
        this.daysUntilExpiry = getDaysUntilExpiry();
        this.showExpiryBanner = true;
      } else {
        this.showExpiryBanner = false;
      }
    },
    dismissExpiryBanner() {
      this.showExpiryBanner = false;
      sessionStorage.setItem('expiry_banner_dismissed', '1');
    },
    goHome() {
      window.location.href = '/';
    },
    toggleMobileMenu(event) {
      event.stopPropagation();
      this.isMobileMenuOpen = !this.isMobileMenuOpen;
    },
    toggleNotification(event) {
      event.stopPropagation();
      this.showNotifications = !this.showNotifications;
      if (this.showNotifications) {
        this.fetchNotifications();
      }
    },
    closeNotifications(restoreFocus) {
      if (!this.showNotifications) return;
      this.showNotifications = false;
      if (restoreFocus && this.$refs.notifTrigger) this.$refs.notifTrigger.focus();
    },
    // Notifications about a bid open that bid; the rest only get marked read.
    openNotificationTarget(note) {
      if (!note || !note.pdf_id) return;
      this.showNotifications = false;
      this.isMobileMenuOpen = false;
      this.$router.push({ name: 'BidDetails', params: { pdf_id: note.pdf_id } });
    },
    async fetchNotifications() {
      const userInfo = localStorage.getItem('user-info');
      if (!userInfo) return;
      
      try {
        const { access_token } = JSON.parse(userInfo);
        const url = this.$API_URL + "/notifications";
        const response = await axios.get(url, {
          headers: { 'Authorization': 'Bearer ' + access_token }
        });
        const fresh = response.data || [];
        this.notifications = fresh;
        this.unreadCount = fresh.filter(n => !n.is_read).length;

        // Surface any notification newer than the highest id we've already seen
        // as a toast. The list is newest-first; ids are monotonic.
        const maxId = fresh.length ? Math.max(...fresh.map(n => n.id)) : 0;
        if (this.lastSeenNotifId === null) {
          // First load — establish the baseline, don't toast history.
          this.lastSeenNotifId = maxId;
        } else if (maxId > this.lastSeenNotifId) {
          fresh
            .filter(n => n.id > this.lastSeenNotifId && !n.is_read)
            .sort((a, b) => a.id - b.id)
            .forEach(n => this.pushToast(n));
          this.lastSeenNotifId = maxId;
        }
      } catch (e) {
        console.error("Error fetching notifications", e);
      }
    },
    pushToast(note) {
      const failed = (note.event_type || '').startsWith('TASK_FAILED');
      const toast = {
        id: note.id,
        noteId: note.id,
        pdfId: note.pdf_id || null,
        title: note.title || (failed ? 'Processing failed' : 'Processing complete'),
        message: note.message || '',
        failed
      };
      this.toasts.push(toast);
      // Auto-dismiss after 8s (failures linger a little longer).
      setTimeout(() => this.dismissToast(toast.id), failed ? 12000 : 8000);
    },
    dismissToast(id) {
      this.toasts = this.toasts.filter(t => t.id !== id);
    },
    onToastClick(toast) {
      const note = this.notifications.find(n => n.id === toast.noteId);
      if (note && !note.is_read) this.markAsRead(note.id);
      this.dismissToast(toast.id);
      this.openNotificationTarget(note || { pdf_id: toast.pdfId });
    },
    handleNotificationClick(note) {
      // Don't hold the navigation for the read flag.
      if (!note.is_read) this.markAsRead(note.id);
      this.openNotificationTarget(note);
    },
    goToNotificationCenter() {
      this.showNotifications = false;
      this.$router.push({ name: 'NotificationCenter' });
    },
    async markAsRead(noteId) {
      const userInfo = localStorage.getItem('user-info');
      if (!userInfo) return;
      
      try {
        const { access_token } = JSON.parse(userInfo);
        const url = `${this.$API_URL}/notifications/${noteId}/read`;
        await axios.post(url, {}, {
          headers: { 'Authorization': 'Bearer ' + access_token }
        });
        // Update local state
        const note = this.notifications.find(n => n.id === noteId);
        if (note) note.is_read = true;
        this.unreadCount = this.notifications.filter(n => !n.is_read).length;
      } catch (e) {
        console.error("Error marking notification as read", e);
      }
    },
    markAllAsRead() {
      this.notifications.forEach(n => {
        if (!n.is_read) this.markAsRead(n.id);
      });
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    handleClickOutside(event) {
      // The component now has multiple root nodes (nav + toast stack), so
      // this.$el is not an element. Query the document instead.
      const notifyContainer = document.querySelector('.notification-wrapper');
      
      if (notifyContainer && !notifyContainer.contains(event.target)) {
        this.showNotifications = false;
      }
    },
    goToAccount() {
      this.isMobileMenuOpen = false;
      this.$router.push({ name: 'Account' });
    },
    goToLogin() {
      this.$router.push({ name: 'LoginPage' });
    },
    goToSignUp() {
      this.$router.push({ name: 'UserRegistration' });
    },
    handleLogout() {
      this.showDropdown = false;
      // Clear user data and redirect to login
      localStorage.removeItem('user-info');
      localStorage.removeItem('user-name');
      this.$router.push({ name: 'LoginPage' });
    },
    fetchUserName() {
      const userInfo = localStorage.getItem('user-info');
      if (userInfo) {
        try {
          const parsed = JSON.parse(userInfo);
          this.userName = parsed.name || parsed.username || parsed.email || 'User';
        } catch (e) {
          console.error("Error parsing user info", e);
        }
      }
    }
  },
  computed: {
    userInitials() {
      const name = this.userName || 'U';
      return name
        .split(' ')
        .map(w => w[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    },
    // Full-width pages that have NO left sidebar menu (bid summary, risk
    // register, obligations, form submission, acceptance & eligibility
    // criteria). The BidSnap AI brand shows in the top header only here;
    // pages WITH the sidebar already carry the brand in the sidebar.
    isSidebarlessPage() {
      return [
        'PdfView', 'RiskRegisterView', 'ObligationsView', 'FormSubmission',
        'AcceptanceCriteria', 'EligibilityCriteria',
      ].includes(this.$route.name);
    },
    // Left offset for the header, expiry banner and action bar: clear the
    // sidebar only on pages that have one.
    shellOffset() {
      return this.isLoggedIn && !this.isSidebarlessPage ? 'var(--sidebar-width, 220px)' : '0';
    }
  }
}
</script>

<style scoped>
/* Plan-expiring-soon banner */
.expiry-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 2rem;
  background: linear-gradient(90deg, #fffbeb, #fef3c7);
  border-bottom: 1px solid #fde68a;
  color: #92400e;
  font-size: 0.85rem;
  font-weight: 600;
  position: sticky;
  top: 64px;
  z-index: 997;
}

.expiry-banner svg {
  flex-shrink: 0;
  color: #d97706;
}

.expiry-banner span {
  flex: 1;
}

.expiry-banner-close {
  flex-shrink: 0;
  border: none;
  background: transparent;
  color: #92400e;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.expiry-banner-close:hover {
  background: rgba(146, 64, 14, 0.1);
}

/* Page action bar — teleport target for page-specific title + buttons */
#page-action-bar:not(:empty) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 2rem;
  height: 48px;
  background: rgba(245, 248, 252, 0.95);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(15, 36, 64, 0.08);
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  position: sticky;
  top: 64px;
  z-index: 998;
  gap: 1rem;
}

#page-action-bar:empty {
  display: none;
}

.logo-glow {
  display: grid;
  place-items: center;
  filter: drop-shadow(0 2px 6px rgba(26, 161, 126, 0.3));
}

/* Brand, bell and user pill are real buttons; drop the native button look. */
.nav-brand-btn,
.notification-trigger,
.user-pill {
  font: inherit;
  color: inherit;
  cursor: pointer;
}

.nav-brand-btn {
  padding: 0;
  border: 0;
  background: transparent;
  border-radius: 10px;
}

.notification-trigger {
  position: relative;
  display: flex;
  align-items: center;
  padding: 0;
  border: 0;
  background: transparent;
  border-radius: 6px;
}

.nav-brand-btn:focus-visible,
.notification-trigger:focus-visible,
.user-pill:focus-visible,
.mark-all-read:focus-visible,
.notification-item-list:focus-visible,
.notification-footer:focus-visible,
.toast-main:focus-visible,
.toast-close:focus-visible,
.expiry-banner-close:focus-visible {
  outline: 2px solid #0071C5;
  outline-offset: 2px;
}

.brand-ai-gradient {
  background: linear-gradient(135deg, #34f0c0 0%, #0ea5e9 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.user-pill-container {
  position: relative;
}

.user-pill {
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
}

.user-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #001F3F;
  color: #ffffff;
  font-size: 0.72rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  letter-spacing: 0.05em;
}

.user-pill:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

/* Login button shown in place of the profile pill when logged out */
.header-login-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  background: #0071C5;
  color: #ffffff;
  border: none;
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 113, 197, 0.3);
}

.header-login-btn:hover {
  background: #005a9e;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 113, 197, 0.4);
}

.user-dropdown {
  position: absolute;
  top: calc(100% + 12px);
  right: 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  padding: 8px;
  z-index: 1000;
  animation: dropdownFadeIn 0.2s ease;
}

@keyframes dropdownFadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  color: #2c3e50;
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.2s ease;
  font-size: 0.95rem;
  font-weight: 500;
}

.dropdown-item:hover {
  background: rgba(0, 113, 197, 0.1);
  color: #0071C5;
}

.dropdown-item.logout:hover {
  background: rgba(231, 76, 60, 0.1);
  color: #e74c3c;
}

.dropdown-item svg {
  flex-shrink: 0;
}

/* Notification Styles */
.notification-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background-color: #b42318;
  color: white;
  border-radius: 50%;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: bold;
  border: 2px solid white;
}

.notification-dropdown {
  position: absolute;
  top: calc(100% + 12px);
  right: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  width: 320px;
  z-index: 1000;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.05);
  animation: dropdownFadeIn 0.2s ease;
}

.notification-header {
  padding: 12px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  font-size: 0.9rem;
  color: #1e293b;
}

.mark-all-read {
  font: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  color: #0071C5;
  background: transparent;
  border: 0;
  padding: 2px 4px;
  border-radius: 6px;
  cursor: pointer;
}

.mark-all-read:hover {
  text-decoration: underline;
}

.notification-list {
  max-height: 400px;
  overflow-y: auto;
}

.no-notifications {
  padding: 20px;
  text-align: center;
  color: #64748b;
  font-size: 0.85rem;
}

.notification-item-list {
  width: 100%;
  font: inherit;
  text-align: left;
  background: transparent;
  border: 0;
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.notification-item-list:hover {
  background: #f8fafc;
}

.notification-item-list.unread {
  background: #f0f9ff;
}

.note-dot {
  width: 8px;
  height: 8px;
  background: #0071C5;
  border-radius: 50%;
  margin-top: 6px;
  flex-shrink: 0;
}

.note-body {
  flex: 1;
}

.note-msg {
  display: block;
  margin: 0;
  font-size: 0.85rem;
  color: #334155;
  line-height: 1.4;
  text-align: left;
}

.note-time {
  font-size: 0.75rem;
  color: #64748b;
  display: block;
  margin-top: 4px;
}

.notification-footer {
  width: 100%;
  font: inherit;
  border: 0;
  padding: 12px 16px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  text-align: center;
  font-weight: 700;
  font-size: 0.85rem;
  color: #0071C5;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.notification-footer:hover {
  background: #f0f9ff;
  color: #0284c7;
}

.notification-footer:hover svg {
  transform: translateX(4px);
}

/* ===== Background-task completion toasts ===== */
.toast-stack {
  position: fixed;
  top: 84px;
  right: 24px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 360px;
  max-width: calc(100vw - 32px);
  pointer-events: none;
}
.toast-card {
  pointer-events: auto;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 14px 14px 16px;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.18);
  border-left: 4px solid #22c55e;
}
/* The clickable part of a toast: opens the bid it is about. */
.toast-main {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 0;
  border: 0;
  background: transparent;
  font: inherit;
  text-align: left;
  cursor: pointer;
  border-radius: 8px;
}
.toast-card.toast-failed { border-left-color: #ef4444; }
.toast-icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #16a34a;
  background: #dcfce7;
}
.toast-failed .toast-icon { color: #dc2626; background: #fee2e2; }
.toast-body { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.toast-title { font-weight: 700; font-size: 0.85rem; color: #0f172a; }
.toast-msg {
  font-size: 0.8rem;
  color: #475569;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.toast-close {
  flex-shrink: 0;
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  padding: 2px;
  border-radius: 6px;
}
.toast-close:hover { color: #475569; background: #f1f5f9; }

@media (prefers-reduced-motion: reduce) {
  .toast-enter-active,
  .toast-leave-active { transition: opacity 0.2s ease; }
  .toast-enter-from,
  .toast-leave-to { transform: none; }
  .notification-dropdown { animation: none; }
}

/* slide-in / fade-out */
.toast-enter-active { transition: all 0.35s cubic-bezier(0.21, 1.02, 0.73, 1); }
.toast-leave-active { transition: all 0.3s ease; }
.toast-enter-from { opacity: 0; transform: translateX(120%); }
.toast-leave-to { opacity: 0; transform: translateX(120%); }
</style>
